import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import './uploadResume.css';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://localhost:7038';

const StepUploadResume = ({ onComplete, onBack, theme }) => {
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const [fileName,  setFileName]  = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!['application/pdf',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
          .includes(file.type)) {
      setError('Please upload a PDF or DOCX file');
      return;
    }

    // Validate file size (5 MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);
    setFileName(file.name);

    try {
      const formData = new FormData();
      formData.append('file', file);          // ← matches your API parameter name

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/resume-builder/parse-upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.status === 401) {
        setError('Session expired. Please log in again.');
        setUploading(false);
        return;
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.message || 'Failed to parse resume');
      }

      const result = await response.json();

      // API returns { wasSuccessful, parsedData, errorMessage }
      if (result?.wasSuccessful) {
        onComplete(result.parsedData);
      } else {
        throw new Error(result?.errorMessage || 'Resume parsing failed');
      }

    } catch (err) {
      setError(err.message || 'Error parsing resume. Please try again.');
      setUploading(false);
      setFileName(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  return (
    <div className={`rup-container ${theme || 'dark'}`}>
      <button className="rup-back-nav" onClick={onBack}>
        ← Back
      </button>

      <div className="rup-header">
        <h1>Upload Your Resume</h1>
        <p>We'll use AI to parse your resume and auto-fill your information. You can edit everything after.</p>
      </div>

      <motion.div
        className="rup-upload-zone"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx"
          onChange={(e) => handleFileUpload(e.target.files[0])}
          style={{ display: 'none' }}
          disabled={uploading}
        />

        <button
          className={`rup-upload-button ${uploading ? 'uploading' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <div className="rup-uploading-state">
              <motion.div
                className="rup-spinner"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              <span className="rup-main-text">Parsing your resume with AI…</span>
              {fileName && (
                <span className="rup-sub-text">📄 {fileName}</span>
              )}
            </div>
          ) : (
            <>
              <div className="rup-icon">📄</div>
              <span className="rup-main-text">Click to upload or drag and drop</span>
              <span className="rup-sub-text">PDF or DOCX · max 5MB</span>
            </>
          )}
        </button>

        {error && (
          <motion.div
            className="rup-error"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
          >
            ⚠️ {error}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default StepUploadResume;
